// Google Places (New), browser-side and OPTIONAL: everything here is inert
// until REACT_APP_GOOGLE_PLACES_KEY is set, so the free OSM search keeps working.

const ENDPOINT = "https://places.googleapis.com/v1/places";
const KEY = process.env.REACT_APP_GOOGLE_PLACES_KEY;
const MALTA_RECTANGLE = {
  rectangle: {
    low: { latitude: 35.78, longitude: 14.16 },
    high: { latitude: 36.12, longitude: 14.6 },
  },
};

export const googlePlacesEnabled = () => Boolean(KEY) && !disabledForSession;

// If the key is present but the API is not enabled/authorised (403), we stop
// calling Google for the rest of the session instead of failing on every keystroke.
let disabledForSession = false;

// One session token per typing session: autocomplete calls share it and the
// single Place Details call closes it (cheapest correct billing pattern).
let sessionToken = null;
const newToken = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

export function endSession() {
  sessionToken = null;
}

export async function googleAutocomplete(input, signal) {
  if (!googlePlacesEnabled()) return null;
  if (!sessionToken) sessionToken = newToken();
  const res = await fetch(`${ENDPOINT}:autocomplete`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Goog-Api-Key": KEY },
    signal,
    body: JSON.stringify({
      input,
      sessionToken,
      includedRegionCodes: ["MT"],
      locationRestriction: MALTA_RECTANGLE,
      regionCode: "MT",
      languageCode: "en",
      includeQueryPredictions: false,
    }),
  });
  if (!res.ok) {
    if (res.status === 403 || res.status === 401) disabledForSession = true;
    throw new Error(`autocomplete_${res.status}`);
  }
  const data = await res.json();
  return (data.suggestions || [])
    .filter((s) => s.placePrediction?.placeId)
    .map((s) => {
      const p = s.placePrediction;
      return {
        id: `g-${p.placeId}`,
        place_id: p.placeId,
        name: p.structuredFormat?.mainText?.text || p.text?.text || "",
        label: p.structuredFormat?.secondaryText?.text || "",
        needs_details: true,
      };
    });
}

/** Rescue for descriptive queries ("MySmile Clinic" when the business is
 *  actually "MySmile Dental Care Centre"). Called ONLY when autocomplete
 *  returned nothing, and it already includes coordinates — no Details call. */
export async function googleTextSearch(input, signal) {
  if (!googlePlacesEnabled()) return null;
  const res = await fetch(`${ENDPOINT}:searchText`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Goog-Api-Key": KEY,
      "X-Goog-FieldMask": "places.id,places.displayName,places.formattedAddress,places.location",
    },
    signal,
    body: JSON.stringify({
      textQuery: input,
      regionCode: "MT",
      languageCode: "en",
      maxResultCount: 5,
      locationRestriction: MALTA_RECTANGLE,
    }),
  });
  if (!res.ok) {
    if (res.status === 403 || res.status === 401) disabledForSession = true;
    throw new Error(`textsearch_${res.status}`);
  }
  const data = await res.json();
  return (data.places || [])
    .filter((p) => p.location)
    .map((p) => ({
      id: `g-${p.id}`,
      name: p.displayName?.text || "",
      label: p.formattedAddress || "",
      lat: p.location.latitude,
      lng: p.location.longitude,
    }));
}

/** Only ever called for the one suggestion the driver taps. */
export async function googlePlaceDetails(placeId) {
  if (!googlePlacesEnabled()) return null;
  const qs = sessionToken ? `?sessionToken=${encodeURIComponent(sessionToken)}` : "";
  const res = await fetch(`${ENDPOINT}/${encodeURIComponent(placeId)}${qs}`, {
    headers: {
      "X-Goog-Api-Key": KEY,
      "X-Goog-FieldMask": "displayName,formattedAddress,location",
    },
  });
  endSession();
  if (!res.ok) throw new Error(`details_${res.status}`);
  const d = await res.json();
  if (!d.location) return null;
  return {
    id: `g-${placeId}`,
    name: d.displayName?.text || "",
    label: d.formattedAddress || "",
    lat: d.location.latitude,
    lng: d.location.longitude,
  };
}
